/**
 * @author NikolaV
 */

import { Ajax } from '@shared/Ajax';
import TileLayer from 'ol/layer/Tile';
import { unByKey } from 'ol/Observable';
import OlXYZSource from 'ol/source/XYZ';
import { MapTileProgressBar } from '@openlayers/MapTileProgressBar';
import { autorun, makeObservable, observable } from 'mobx';
import BaseLayer from 'ol/layer/Base';
import { TreeNode } from '@shared/ui-native/Tree';
import { VectorDrawing } from './drawings/vector-drawing';
import { IDrawing } from './drawings/drawing';
import { RasterDrawing } from './drawings/raster-drawing';


export interface IMapTileResponse {
     baseUrl: string;
     bounds: IBounds;
     minZoom: number;
     maxZoom: number;
     tileSize: number;
     valid: boolean;
     layers: string;
     cs: number;
     v: number;
     status: boolean;
     rasterImages?: any[];
 }


 // for every zoom level we have a bounding rect
 // so we can know not to requests empty tiles when map view is outside of bounds
 export interface IBounds {
     1: number[][];
     2: number[][];
     3: number[][];
     4: number[][];
     5: number[][];
     6: number[][];
     7: number[][];
     8: number[][];
     9: number[][];
     10: number[][];
     11: number[][];
     12: number[][];
     13: number[][];
     14: number[][];
     15: number[][];
     16: number[][];
     17: number[][];
     18: number[][];
     19: number[][];
     20: number[][];
     21: number[][];
     22: number[][];
     23: number[][];
 }


 export class BackgroundMaps {
     private worksiteMap = '';
     private alignment = '';
     private logpointFolder = '';
     private rasterizedModelIds = new Set<number>(); // models from filetree that will show as mesh
     private models: string[] = [];
     private overlays = [];
     private lastRequestLayers = '';

     private geoserverLayers = [];
     private activeLayers: BaseLayer[] = [];

     private activeDrawings: IDrawing[] = [];

     // openlayers
     public readonly layer = new TileLayer();
     @observable layerOpacity = 100;

     // progress bar
     private bgMapsLayerEventKeys: (OlEventsKey | OlEventsKey[])[] = [];

     constructor(public progressBar: MapTileProgressBar) {
         makeObservable(this);
         autorun(() => {
             this.setOpacity(this.layerOpacity);
         });
     }

     getJoinedString(): string {
         const all = [...this.models, this.worksiteMap, this.alignment, this.logpointFolder];

         this.rasterizedModelIds.forEach(id => {
             all.push('m_' + id);
         });

         return all.join(',');
     }

     getTileUrl(baseUrl, id, coord, zoom, cs): string {
         const url = [
             kuura.getContextPath() + baseUrl,
             '?id=', id,
             '&x=', coord.x,
             '&y=', coord.y,
             '&z=', zoom,
             '&cs=', cs,
         ].join('');

         return url;
     }

     getCombinedTileUrl(baseUrl, layers, coord, zoom, cs, v): string {
         const path = (v >= 3 ? kuura.getMapsPath() : kuura.getContextPath());
         const csString = (v >= 4 ? '' : '&cs=' + cs);
         const url = [
             path + baseUrl,
             '?layers=', layers,
             '&x=', coord.x,
             '&y=', coord.y,
             '&z=', zoom,
             csString,
             '&v=', v,
         ].join('');

         return url;
     }

     getLayers(): string {
         return this.models.join(',');
     }

     add(id: number | string) {
         if ($.isNumeric(id)) {
             this.addModel(id as number);
         }
         else {
             if (!this.models.includes(id as string)) {
                 this.models.push(id as string);
             }
         }
     }

     reloadWithIds = (ids: number[]) => {
         this.clearModels(false);
         ids.forEach(id => this.add(id));
         return this.load();
     };

     addTypedMap(type: 'm' | 'o' | 'r', id: number) {
         if (!$.isNumeric(id)) return;

         const model = type + '_' + id;

         if (!this.models.includes(model)) {
             this.models.push(model);
         }
     }

     async createLayerForModel(node: TreeNode): Promise<BaseLayer> {
         const drawing = new VectorDrawing(node);

         await drawing.load();

         const layer = drawing.layerGroup;
         layer.setOpacity(utils.clamp(0.2, this.layerOpacity / 100, 1));
         this.activeLayers.push(layer);
         this.activeDrawings.push(drawing);
         return drawing.layerGroup;
     }


     async createRasterLayerForModel(node: TreeNode, isMobile = false): Promise<BaseLayer> {

        if (this.activeDrawings.find(drawing => drawing.uuid === node.uuid)) {
            return;
        }


        const drawing = new RasterDrawing(node, isMobile);

        await drawing.load();

        const layer = drawing.layerGroup;
        layer.setOpacity(utils.clamp(0.2, this.layerOpacity / 100, 1));
        this.activeLayers.push(layer);
        this.activeDrawings.push(drawing);
        return drawing.layerGroup;
     }

     removeDrawing(drawingUuid: string): void {
        const drawingToBeRemoved = this.activeDrawings.find(drawing => drawing.uuid === drawingUuid);

        if (!drawingToBeRemoved) return;

        drawingToBeRemoved.dispose();
        this.activeDrawings = this.activeDrawings.filter(drawing => drawing.uuid !== drawingUuid);
     }

     addModel(modelId: number) {
         this.addTypedMap('m', modelId);
     }

     addOutline(modelId: number) {
         this.addTypedMap('o', modelId);
     }

     addRasterImage(modelId: number) {
         this.addTypedMap('r', modelId);
     }

     getModelIds = (): number[] => {
         return this.models.map(mapId => parseInt(mapId.match(/\d+$/gi)[0], 10));
     }

     toggleModel(modelId: number) {
         const model = 'm_' + modelId;
         const index = this.models.indexOf(model);

         if (index > -1) {
             this.hideModel(modelId);
         }
         else {
             this.showModel(modelId);
         }
     }

     hideModel(modelId: number) {
         const model = 'm_' + modelId;
         const index = this.models.indexOf(model);

         if (index > -1) {
             this.models.splice(index, 1);
         }

         this.load();
     }

     showModel(modelId: number) {
         this.addModel(modelId);

         this.load();
     }

     clearModels(reload = true) {
         this.models = [];

         if (reload) {
             this.load();
         }
     }

     getRasterizedModelIds(): Set<number> {
         return this.rasterizedModelIds;
     }

     toggleRasterizedModelId(modelId: number) {
         if (this.rasterizedModelIds.has(modelId)) {
             this.rasterizedModelIds.delete(modelId);
         } else {
             this.rasterizedModelIds.add(modelId);
         }
         this.load();
     }

     createSource(mapTileResponse: IMapTileResponse): OlXYZSource {
         const { baseUrl, layers, minZoom, maxZoom, tileSize, v, cs, bounds } = mapTileResponse;

         const source = new OlXYZSource({
             tileUrlFunction: (coord: any) => {
                 const [zoom, x, y] = coord;

                 // if position is out of bounds, don't request the tile
                 if (zoom < minZoom || zoom > maxZoom || !bounds[zoom] ||
                     bounds[zoom][0][0] > x || x > bounds[zoom][1][0] ||
                     bounds[zoom][0][1] > y || y > bounds[zoom][1][1]) {
                     return undefined;
                 }

                 return `${kuura.getContextPath()}${baseUrl}?layers=${layers}&cs=${cs}&v=${v}&x=${x}&y=${y}&z=${zoom}`;
             },
             minZoom,
             maxZoom,
             tileSize,
         });

         return source;
     }

     getExtentForDrawing(uuid: string) {
        const drawing = this.activeDrawings.find(drawing => drawing.uuid === uuid);

        if (!drawing) return null;

        return drawing.extent;
     }

     load = _.debounce((): JQueryPromise<any> => {
         const mapIdsJoined = this.getJoinedString();
         if (this.lastRequestLayers == mapIdsJoined) return;

         return Ajax({
             url: `gmaps_tile_bounds.json`,
             data: {
                 layers: mapIdsJoined,
                 projectId: kuura.getProjectId(),
             },
         }).done((resp: IMapTileResponse) => {
             if (resp.layers) {
                 const source = this.createSource(resp);

                 if (this.progressBar) {
                     this.bgMapsLayerEventKeys.forEach(unByKey);
                     this.progressBar.reset();
                     this.bgMapsLayerEventKeys = this.progressBar.attachEvents(source);
                 }
                 this.layer.setSource(source);
                 this.lastRequestLayers = mapIdsJoined;
                 setTimeout(() => { // sometimes setSource will not refresh so we have to force it
                     this.layer.getSource().refresh();
                 }, 100);
             }
         });
     }, 100);

     clearLogpoints(reload = true) {
         this.logpointFolder = '';

         if (reload) {
             this.load();
         }
     }

     loadLogpoints(folderId: number) {
         this.logpointFolder = 'l_' + folderId;
         this.load();
     }

     setAlignment(modelId: number) {
         this.alignment = 'a_' + modelId;
     }

     loadAlignment(modelId: number) {
         this.setAlignment(modelId);
         this.load();
     }

     loadWorksite() {
         const defaultMap = kuura.getProjectWorksiteMapModel();
         if (defaultMap?.id) {
             this.worksiteMap = 'm_' + defaultMap?.id;
             this.load();
         }
     }

     setOpacity = (opacity: number) => {
         if (typeof opacity === 'number') {
             this.activeLayers.forEach(layer => layer.setOpacity(utils.clamp(0.2, opacity / 100, 1)));
         } else {
             this.activeLayers.forEach(layer => layer.setOpacity(1));

         }
     }

     getOpacity = () => {
         return this.layerOpacity;
     }
 }

